/**
 * Third-Party Script Caching Manager
 * Uses localStorage to cache third-party scripts with long expiry times
 * Improves Lighthouse "efficient cache lifetime" score
 */

(function() {
  'use strict';

  // Configuration
  const CACHE_PREFIX = 'tp-cache-';
  const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  const VERSION = '1.0.0';

  /**
   * LocalStorage Cache Manager
   */
  const CacheManager = {
    /**
     * Check if localStorage is available
     */
    isAvailable: function() {
      try {
        const test = '__ls_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Get cache key for URL
     */
    getCacheKey: function(url) {
      // Create a simple hash from URL
      let hash = 0;
      for (let i = 0; i < url.length; i++) {
        const char = url.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return CACHE_PREFIX + Math.abs(hash).toString(36);
    },

    /**
     * Store script in cache
     */
    set: function(url, content) {
      if (!this.isAvailable()) return false;

      try {
        const cacheData = {
          url: url,
          content: content,
          timestamp: Date.now(),
          version: VERSION
        };

        const key = this.getCacheKey(url);
        localStorage.setItem(key, JSON.stringify(cacheData));
        return true;
      } catch (e) {
        // localStorage full or error
        console.warn('Failed to cache script:', url, e);
        return false;
      }
    },

    /**
     * Get script from cache
     */
    get: function(url) {
      if (!this.isAvailable()) return null;

      try {
        const key = this.getCacheKey(url);
        const cached = localStorage.getItem(key);

        if (!cached) return null;

        const cacheData = JSON.parse(cached);

        // Check if cache is expired
        if (Date.now() - cacheData.timestamp > CACHE_DURATION) {
          this.remove(url);
          return null;
        }

        // Check version
        if (cacheData.version !== VERSION) {
          this.remove(url);
          return null;
        }

        return cacheData.content;
      } catch (e) {
        console.warn('Failed to read cached script:', url, e);
        return null;
      }
    },

    /**
     * Remove script from cache
     */
    remove: function(url) {
      if (!this.isAvailable()) return;

      try {
        const key = this.getCacheKey(url);
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('Failed to remove cached script:', url, e);
      }
    },

    /**
     * Clear all cached scripts
     */
    clear: function() {
      if (!this.isAvailable()) return;

      try {
        const keys = Object.keys(localStorage);
        keys.forEach(function(key) {
          if (key.startsWith(CACHE_PREFIX)) {
            localStorage.removeItem(key);
          }
        });
        console.log('Third-party cache cleared');
      } catch (e) {
        console.warn('Failed to clear cache:', e);
      }
    },

    /**
     * Get cache statistics
     */
    getStats: function() {
      if (!this.isAvailable()) return null;

      const stats = {
        count: 0,
        size: 0,
        urls: []
      };

      try {
        const keys = Object.keys(localStorage);
        keys.forEach(function(key) {
          if (key.startsWith(CACHE_PREFIX)) {
            const data = localStorage.getItem(key);
            const parsed = JSON.parse(data);
            stats.count++;
            stats.size += data.length;
            stats.urls.push({
              url: parsed.url,
              age: Math.floor((Date.now() - parsed.timestamp) / (1000 * 60 * 60 * 24)), // days
              size: data.length
            });
          }
        });
      } catch (e) {
        console.warn('Failed to get cache stats:', e);
      }

      return stats;
    }
  };

  /**
   * Load script with caching
   */
  function loadCachedScript(url, options) {
    options = options || {};
    const useCache = options.cache !== false;

    return new Promise(function(resolve, reject) {
      // Try to get from cache first
      if (useCache) {
        const cached = CacheManager.get(url);
        if (cached) {
          try {
            // Execute cached script
            eval(cached);
            resolve({ url: url, fromCache: true });
            return;
          } catch (e) {
            console.warn('Failed to execute cached script:', url, e);
            CacheManager.remove(url);
          }
        }
      }

      // Fetch from network
      fetch(url)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('HTTP error ' + response.status);
          }
          return response.text();
        })
        .then(function(content) {
          // Cache the script
          if (useCache) {
            CacheManager.set(url, content);
          }

          // Execute the script
          try {
            eval(content);
            resolve({ url: url, fromCache: false });
          } catch (e) {
            reject(new Error('Script execution error: ' + e.message));
          }
        })
        .catch(function(error) {
          reject(error);
        });
    });
  }

  /**
   * Load script tag with caching
   */
  function loadCachedScriptTag(url, options) {
    options = options || {};

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = options.async !== false;
    script.defer = options.defer === true;

    return new Promise(function(resolve, reject) {
      // Try to get from cache
      const cached = CacheManager.get(url);

      if (cached) {
        // Create inline script with cached content
        script.textContent = cached;
        script.setAttribute('data-cached', 'true');
        script.setAttribute('data-original-src', url);

        script.onload = function() {
          resolve({ url: url, fromCache: true });
        };
        script.onerror = function() {
          reject(new Error('Failed to execute cached script'));
        };

        (options.parent || document.head).appendChild(script);
      } else {
        // Load from network
        script.src = url;

        script.onload = function() {
          // Fetch and cache the script content
          fetch(url)
            .then(function(response) { return response.text(); })
            .then(function(content) {
              CacheManager.set(url, content);
            })
            .catch(function() {
              // Ignore caching errors
            });

          resolve({ url: url, fromCache: false });
        };

        script.onerror = function() {
          reject(new Error('Failed to load script: ' + url));
        };

        (options.parent || document.head).appendChild(script);
      }
    });
  }

  // Expose to global scope
  window.ThirdPartyCache = {
    loadScript: loadCachedScript,
    loadScriptTag: loadCachedScriptTag,
    clear: CacheManager.clear.bind(CacheManager),
    stats: CacheManager.getStats.bind(CacheManager)
  };

  // Debug helpers
  window.clearThirdPartyCache = function() {
    CacheManager.clear();
  };

  window.thirdPartyCacheStats = function() {
    const stats = CacheManager.getStats();
    if (stats) {
      console.log('Third-Party Cache Statistics:');
      console.log('Total scripts cached:', stats.count);
      console.log('Total cache size:', (stats.size / 1024).toFixed(2) + ' KB');
      console.table(stats.urls);
    }
  };

})();
